import { useMemo } from 'react';
import { sanitizeNavigationUrl, wait } from '@bridge/utils';
import { useStytchB2BClient, useStytchMemberSession } from '@stytch/nextjs/b2b';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import { buildLoginPath } from 'app/authenticate/_lib/auth-redirect-helpers';
import { isDummyAuth } from 'lib/env';
import { getSession } from 'server/actions/session';
import { useModalStore } from 'store/modal-store';
import { useSessionContext } from 'store/session-store';
import { useSheetStore } from 'store/sheet-store';
import { SESSION_DISMISSED_KEY_PREFIX } from 'components/dashboard-alerts/useDashboardAlertDismiss';
export const useSession = () => {
  const router = useRouter();
  const stytchClient = useStytchB2BClient();
  // Dev feature overrides are applied server-side by getSession() and the
  // panel's `router.refresh()` propagates each toggle back into this store via
  // SessionProvider, so no client-side merge is needed here.
  const session = useSessionContext(state => state.session);
  const setSession = useSessionContext(state_0 => state_0.setSession);
  const setIsLoggingOut = useSessionContext(state_1 => state_1.setIsLoggingOut);
  const {
    session: stytchSession,
    isInitialized: isStytchInitialized
  } = useStytchMemberSession();
  const isUnauthorized = useMemo(() => isStytchInitialized && !stytchSession, [isStytchInitialized, stytchSession]);
  const isLoggedIn = useMemo(() => isStytchInitialized && !!stytchSession, [isStytchInitialized, stytchSession]);
  async function logout(redirectPath?: string) {
    setIsLoggingOut(true);
    if (isDummyAuth) {
      // The dummy session cookie is httpOnly — only the /login/dummy route
      // can clear it. Full navigation, not router.replace, so the deletion
      // lands before /login's cookie-presence redirect runs.
      window.location.assign('/login/dummy?signout=true');
      return;
    }
    if (stytchSession) {
      try {
        await stytchClient.session.revoke();
      } catch (error) {
        console.warn('Error revoking session', error);
      }
    }
    router.replace(sanitizeNavigationUrl(buildLoginPath(redirectPath)));
    await wait(2000);
    wipeSession();
  }
  async function refreshSession() {
    const freshSession = await getSession();
    setSession(freshSession);
  }
  function wipeSession() {
    toast.remove();
    setSession(null);
    Object.keys(sessionStorage).filter(key => key.startsWith(SESSION_DISMISSED_KEY_PREFIX)).forEach(key_0 => sessionStorage.removeItem(key_0));
    useModalStore.getState().closeAllModals();
    useSheetStore.getState().closeAllSheets();
  }
  return {
    session,
    setSession,
    logout,
    refreshSession,
    isUnauthorized,
    isStytchInitialized,
    wipeSession,
    isLoggedIn
  };
};